<section class="page">
  <header class="page-header">
    <div>
      <h1>PM Reports</h1>
      <p>Dashboards, burndown, burnup, velocity, workload, and deadline compliance.</p>
    </div>
    <div class="header-actions">
      <button mat-stroked-button type="button" (click)="refreshDeadlines()">
        <mat-icon>update</mat-icon> Refresh deadlines
      </button>
      <button mat-stroked-button type="button" [disabled]="!selectedProjectId() || exporting() === 'roadmap'" (click)="exportCsv('roadmap')">
        <mat-icon>download</mat-icon> Export roadmap
      </button>
      <mat-form-field appearance="outline">
        <mat-label>Project</mat-label>
        <mat-select [value]="selectedProjectId()" (selectionChange)="onProjectChange($event.value)">
          @for (p of projects(); track p.id) {
            <mat-option [value]="p.id">{{ p.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
  </header>

  @if (error()) { <div class="error">{{ error() }}</div> }
  @if (loading()) { <div class="loading"><mat-spinner diameter="36"></mat-spinner></div> }

  @if (summary(); as s) {
    <div class="stats">
      <div class="stat"><span>Total</span><strong>{{ s.totalIssues }}</strong></div>
      <div class="stat"><span>Open</span><strong>{{ s.openIssues }}</strong></div>
      <div class="stat"><span>In progress</span><strong>{{ s.inProgressIssues }}</strong></div>
      <div class="stat"><span>Done</span><strong>{{ s.doneIssues }}</strong></div>
      <div class="stat"><span>Unassigned</span><strong>{{ s.unassignedIssues }}</strong></div>
      <div class="stat"><span>Completion</span><strong>{{ s.completionPercent ?? 0 }}%</strong></div>
      @if (s.unreleasedVersions != null) {
        <div class="stat"><span>Unreleased versions</span><strong>{{ s.unreleasedVersions }}</strong></div>
      }
    </div>
  }

  @if (deadlineDash(); as d) {
    <div class="stats deadline-stats">
      <div class="stat"><span>Due today</span><strong>{{ d.dueToday }}</strong></div>
      <div class="stat"><span>Due tomorrow</span><strong>{{ d.dueTomorrow }}</strong></div>
      @if (d.dueThisWeek != null) {
        <div class="stat"><span>Due this week</span><strong>{{ d.dueThisWeek }}</strong></div>
      }
      <div class="stat"><span>Upcoming</span><strong>{{ d.upcomingDeadlines }}</strong></div>
      <div class="stat overdue"><span>Overdue</span><strong>{{ d.overdue }}</strong></div>
      @if (d.blocked != null) { <div class="stat"><span>Blocked</span><strong>{{ d.blocked }}</strong></div> }
      @if (d.atRisk != null) { <div class="stat warn"><span>At risk</span><strong>{{ d.atRisk }}</strong></div> }
      @if (d.critical != null) { <div class="stat overdue"><span>Critical</span><strong>{{ d.critical }}</strong></div> }
      <div class="stat"><span>Completed today</span><strong>{{ d.completedToday }}</strong></div>
      <div class="stat"><span>Delayed</span><strong>{{ d.delayed }}</strong></div>
      <div class="stat"><span>Avg delay</span><strong>{{ d.averageDelayDays }}d</strong></div>
      <div class="stat"><span>Health</span><strong>{{ d.overallHealthLabel }}</strong></div>
    </div>
  }

  @if (projectHealth(); as h) {
    <section class="health-section">
      <h2 class="section-title">Project health engine</h2>
      <div class="health-rag-banner" [ngClass]="healthRagClass(h.healthLabel, h.healthScore)">
        <strong>{{ h.healthLabel }}</strong>
        @if (h.healthScore != null) { <span>Score {{ h.healthScore }}</span> }
      </div>
      <div class="stats health-metrics">
        <div class="stat" [ngClass]="healthRagClass(h.healthLabel, h.healthScore)">
          <span>Health score</span><strong>{{ h.healthScore ?? '—' }}</strong>
        </div>
        <div class="stat"><span>Risk score</span><strong>{{ h.riskScore ?? '—' }}</strong></div>
        <div class="stat overdue"><span>Overdue</span><strong>{{ h.overdue }} ({{ formatPercent(h.overduePercent) }})</strong></div>
        <div class="stat"><span>Blocked</span><strong>{{ h.blocked }} ({{ formatPercent(h.blockedPercent) }})</strong></div>
        <div class="stat"><span>Completion</span><strong>{{ formatPercent(h.completionPercent) }}</strong></div>
        <div class="stat" [ngClass]="velocityTrendClass(h.velocityTrend)">
          <span>Velocity trend</span><strong>{{ velocityTrendLabel(h.velocityTrend) }}</strong>
        </div>
      </div>
      <div class="stats">
        <div class="stat"><span>Project tasks</span><strong>{{ h.totalTasks }}</strong></div>
        <div class="stat"><span>Completed</span><strong>{{ h.completed }}</strong></div>
        <div class="stat"><span>Pending</span><strong>{{ h.pending }}</strong></div>
        <div class="stat"><span>Delayed</span><strong>{{ h.delayed }}</strong></div>
        <div class="stat"><span>Avg delay</span><strong>{{ h.averageDelayDays }}d</strong></div>
      </div>
    </section>
  }

  @if (projectSla(); as sla) {
    <div class="stats sla-stats">
      <div class="stat"><span>SLA on-time</span><strong>{{ sla.onTimePercent ?? '—' }}@if (sla.onTimePercent != null) {%}</strong></div>
      <div class="stat"><span>Overdue rate</span><strong>{{ sla.overduePercent ?? '—' }}@if (sla.overduePercent != null) {%}</strong></div>
      <div class="stat"><span>Avg resolution</span><strong>@if (sla.averageResolutionDays != null) { {{ sla.averageResolutionDays }}d } @else { — }</strong></div>
      <div class="stat" [class.overdue]="sla.metSla === false"><span>SLA target</span><strong>{{ sla.slaTargetPercent ?? '—' }}@if (sla.slaTargetPercent != null) {%}</strong></div>
      <div class="stat"><span>Status</span><strong>{{ sla.healthLabel || (sla.metSla ? 'Met' : 'At risk') }}</strong></div>
    </div>
  }

  <mat-tab-group>
    <mat-tab label="Charts">
      <div class="tab-body">
        <mat-form-field appearance="outline">
          <mat-label>Sprint</mat-label>
          <mat-select [value]="selectedSprintId()" (selectionChange)="onSprintChange($event.value)">
            @for (sp of sprints(); track sp.id) {
              <mat-option [value]="sp.id">{{ sp.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <div class="charts">
          @if (burndown(); as b) {
            <section class="chart-card">
              <h3>Burndown — {{ b.sprintName }}</h3>
              <svg class="svg-chart" viewBox="0 0 320 160" role="img" aria-label="Burndown chart">
                <polyline fill="none" stroke="#cbd5e1" stroke-width="2" [attr.points]="svgLine(b.idealLine)" />
                <polyline fill="none" stroke="#0f766e" stroke-width="2.5" [attr.points]="svgLine(b.remainingLine)" />
              </svg>
              <p class="legend"><span class="swatch remaining"></span> Remaining <span class="swatch ideal"></span> Ideal · Total {{ b.totalPoints }} pts</p>
            </section>
          }

          @if (burnup(); as b) {
            <section class="chart-card">
              <h3>Burnup — {{ b.sprintName }}</h3>
              <svg class="svg-chart" viewBox="0 0 320 160" role="img" aria-label="Burnup chart">
                <polyline fill="none" stroke="#94a3b8" stroke-width="2" stroke-dasharray="4 3" [attr.points]="svgLine(b.scopeLine)" />
                <polyline fill="none" stroke="#2563eb" stroke-width="2.5" [attr.points]="svgLine(b.completedLine)" />
              </svg>
              <p class="legend">Scope {{ b.scopePoints }} pts</p>
            </section>
          }

          @if (velocity(); as v) {
            <section class="chart-card">
              <h3>Velocity (avg {{ v.averageCompleted }})</h3>
              <svg class="svg-chart" viewBox="0 0 320 160" role="img" aria-label="Velocity chart">
                @for (bar of svgVelocityBars(v.sprints); track bar.label) {
                  <rect [attr.x]="bar.x" [attr.y]="140 - bar.committedH" width="14" [attr.height]="bar.committedH" fill="#cbd5e1" rx="2" />
                  <rect [attr.x]="bar.x + 16" [attr.y]="140 - bar.completedH" width="14" [attr.height]="bar.completedH" fill="#0f766e" rx="2" />
                }
              </svg>
              <ul class="plain-list">
                @for (s of v.sprints; track s.sprintId) {
                  <li><strong>{{ s.name }}</strong> — committed {{ s.committedPoints }}, completed {{ s.completedPoints }}</li>
                } @empty { <li class="muted">No completed sprints yet.</li> }
              </ul>
            </section>
          }

          @if (sprintHealth(); as sh) {
            <section class="chart-card">
              <h3>Sprint health — {{ sh.sprintName }}</h3>
              <div class="health-rag-banner" [ngClass]="healthRagClass(sh.healthLabel)">
                <strong>{{ sh.healthLabel || sh.health }}</strong>
                <span>{{ sh.completionPercent }}% complete</span>
              </div>
              <div class="stats">
                <div class="stat"><span>Issues</span><strong>{{ sh.completedIssues }}/{{ sh.totalIssues }}</strong></div>
                <div class="stat"><span>Points</span><strong>{{ sh.completedPoints }}/{{ sh.committedPoints }}</strong></div>
                <div class="stat overdue"><span>Overdue</span><strong>{{ sh.overdueIssues }}</strong></div>
                <div class="stat"><span>Blocked</span><strong>{{ sh.blockedIssues }}</strong></div>
                <div class="stat"><span>Delay</span><strong>{{ sh.sprintDelayDays }}d</strong></div>
              </div>
            </section>
          }
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Overdue report">
      <div class="tab-body">
        <table mat-table [dataSource]="overdueRows()" class="full">
          <ng-container matColumnDef="keyOrTitle"><th mat-header-cell *matHeaderCellDef>Item</th><td mat-cell *matCellDef="let r">{{ r.keyOrTitle }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel }}</td></ng-container>
          <ng-container matColumnDef="assigneeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let r">{{ r.assigneeName || '—' }}</td></ng-container>
          <ng-container matColumnDef="dueDate"><th mat-header-cell *matHeaderCellDef>Deadline</th><td mat-cell *matCellDef="let r">{{ r.dueDate || '—' }}</td></ng-container>
          <ng-container matColumnDef="deadlineBadge"><th mat-header-cell *matHeaderCellDef>Badge</th><td mat-cell *matCellDef="let r"><span class="deadline-badge" [ngClass]="badgeClass(r.deadlineBadge || r.delayLabel, r.isOverdue)">{{ r.deadlineBadge || r.remainingLabel || '—' }}</span></td></ng-container>
          <ng-container matColumnDef="delayLabel"><th mat-header-cell *matHeaderCellDef>Delay</th><td mat-cell *matCellDef="let r">{{ r.delayLabel }}</td></ng-container>
          <ng-container matColumnDef="remainingLabel"><th mat-header-cell *matHeaderCellDef>Remaining</th><td mat-cell *matCellDef="let r">{{ r.remainingLabel }}</td></ng-container>
          <ng-container matColumnDef="escalation"><th mat-header-cell *matHeaderCellDef>Escalation</th><td mat-cell *matCellDef="let r">@if (r.escalationLevel) { <span class="escalation-badge" [ngClass]="escalationClass(r.escalationLevel)">{{ r.escalationLabel || escalationLabel(r.escalationLevel) }}</span> } @else { — }</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="reportColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: reportColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Delay report">
      <div class="tab-body">
        <table mat-table [dataSource]="delayRows()" class="full">
          <ng-container matColumnDef="keyOrTitle"><th mat-header-cell *matHeaderCellDef>Item</th><td mat-cell *matCellDef="let r">{{ r.keyOrTitle }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel }}</td></ng-container>
          <ng-container matColumnDef="assigneeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let r">{{ r.assigneeName || '—' }}</td></ng-container>
          <ng-container matColumnDef="dueDate"><th mat-header-cell *matHeaderCellDef>Deadline</th><td mat-cell *matCellDef="let r">{{ r.dueDate || '—' }}</td></ng-container>
          <ng-container matColumnDef="deadlineBadge"><th mat-header-cell *matHeaderCellDef>Badge</th><td mat-cell *matCellDef="let r"><span class="deadline-badge" [ngClass]="badgeClass(r.deadlineBadge || r.delayLabel, r.isOverdue)">{{ r.deadlineBadge || r.remainingLabel || '—' }}</span></td></ng-container>
          <ng-container matColumnDef="delayLabel"><th mat-header-cell *matHeaderCellDef>Delay</th><td mat-cell *matCellDef="let r">{{ r.delayLabel }}</td></ng-container>
          <ng-container matColumnDef="remainingLabel"><th mat-header-cell *matHeaderCellDef>Remaining</th><td mat-cell *matCellDef="let r">{{ r.remainingLabel }}</td></ng-container>
          <ng-container matColumnDef="escalation"><th mat-header-cell *matHeaderCellDef>Escalation</th><td mat-cell *matCellDef="let r">@if (r.escalationLevel) { <span class="escalation-badge" [ngClass]="escalationClass(r.escalationLevel)">{{ r.escalationLabel || escalationLabel(r.escalationLevel) }}</span> } @else { — }</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="reportColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: reportColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Employee performance">
      <div class="tab-body">
        <table mat-table [dataSource]="employeePerf()" class="full">
          <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Employee</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
          <ng-container matColumnDef="completedBeforeDeadline"><th mat-header-cell *matHeaderCellDef>Before deadline</th><td mat-cell *matCellDef="let r">{{ r.completedBeforeDeadline }}</td></ng-container>
          <ng-container matColumnDef="completedOnTime"><th mat-header-cell *matHeaderCellDef>On time</th><td mat-cell *matCellDef="let r">{{ r.completedOnTime }}</td></ng-container>
          <ng-container matColumnDef="completedLate"><th mat-header-cell *matHeaderCellDef>Late</th><td mat-cell *matCellDef="let r">{{ r.completedLate }}</td></ng-container>
          <ng-container matColumnDef="averageDelayDays"><th mat-header-cell *matHeaderCellDef>Avg delay</th><td mat-cell *matCellDef="let r">{{ r.averageDelayDays }}</td></ng-container>
          <ng-container matColumnDef="overdueTasks"><th mat-header-cell *matHeaderCellDef>Overdue</th><td mat-cell *matCellDef="let r">{{ r.overdueTasks }}</td></ng-container>
          <ng-container matColumnDef="completionRatePercent"><th mat-header-cell *matHeaderCellDef>Completion %</th><td mat-cell *matCellDef="let r">{{ r.completionRatePercent }}%</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="perfColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: perfColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Team workload">
      <div class="tab-body">
        <table mat-table [dataSource]="workload()" class="full">
          <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Person</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
          <ng-container matColumnDef="openCount"><th mat-header-cell *matHeaderCellDef>Open</th><td mat-cell *matCellDef="let r">{{ r.openCount }}</td></ng-container>
          <ng-container matColumnDef="inProgressCount"><th mat-header-cell *matHeaderCellDef>In progress</th><td mat-cell *matCellDef="let r">{{ r.inProgressCount }}</td></ng-container>
          <ng-container matColumnDef="doneCount"><th mat-header-cell *matHeaderCellDef>Done</th><td mat-cell *matCellDef="let r">{{ r.doneCount }}</td></ng-container>
          <ng-container matColumnDef="remainingStoryPoints"><th mat-header-cell *matHeaderCellDef>Remaining pts</th><td mat-cell *matCellDef="let r">{{ r.remainingStoryPoints }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="workloadColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: workloadColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Dashboards">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Dashboard name</mat-label><input matInput [(ngModel)]="dashboardName"></mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="createDashboard()">Create</button>
          <button mat-stroked-button type="button" (click)="createRolePresets()">
            <mat-icon>dashboard_customize</mat-icon> Create shared role presets
          </button>
        </div>
        <div class="saved">
          @for (d of dashboards(); track d.id) {
            <button mat-stroked-button type="button" (click)="openDashboard(d.id)">
              {{ d.name }} ({{ d.widgetCount }})
              @if (d.isShared) { <span class="shared-tag">shared</span> }
            </button>
            <button mat-icon-button type="button" (click)="deleteDashboard(d.id)"><mat-icon>delete</mat-icon></button>
          }
        </div>
        @if (dashboard(); as dash) {
          <section class="chart-card">
            <h3>{{ dash.name }} @if (dash.isShared) { <span class="shared-tag">tenant shared</span> }</h3>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Widget title</mat-label><input matInput [(ngModel)]="widgetTitle"></mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Type</mat-label>
                <mat-select [(ngModel)]="widgetType">
                  @for (t of widgetTypes; track t.value) { <mat-option [value]="t.value">{{ t.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="addWidget()">Add widget</button>
            </div>
            <div class="widget-grid">
              @for (w of dash.widgets; track w.id) {
                <article class="widget-card">
                  <header class="widget-head">
                    <strong>{{ w.title }}</strong>
                    <span class="widget-type">{{ w.widgetTypeLabel || widgetTypeLabel(w.widgetType) }}</span>
                    <button mat-icon-button type="button" (click)="deleteWidget(w.id)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </header>
                  @if (widgetDataFor(w.id); as data) {
                    @switch (w.widgetType) {
                      @case (0) {
                        @if (data.summary; as s) {
                          <div class="mini-stats">
                            <span>Open {{ s.openIssues }}</span>
                            <span>Done {{ s.doneIssues }}</span>
                            <span>{{ s.completionPercent ?? 0 }}%</span>
                          </div>
                        }
                      }
                      @case (1) {
                        @if (data.burndown; as b) {
                          <div class="bars compact">
                            @for (p of b.remainingLine; track p.label) {
                              <div class="bar-col" [title]="p.label + ': ' + p.value">
                                <div class="bar remaining" [style.height.%]="(p.value / maxPoint(b.remainingLine)) * 100"></div>
                              </div>
                            }
                          </div>
                          <p class="legend">{{ b.sprintName }} · {{ b.totalPoints }} pts</p>
                        }
                      }
                      @case (2) {
                        @if (data.burnup; as b) {
                          <div class="bars compact">
                            @for (p of b.completedLine; track p.label) {
                              <div class="bar-col">
                                <div class="bar completed" [style.height.%]="(p.value / maxPoint(b.completedLine)) * 100"></div>
                              </div>
                            }
                          </div>
                        }
                      }
                      @case (3) {
                        @if (data.velocity; as v) {
                          <p class="legend">Avg {{ v.averageCompleted }} · {{ v.sprints.length }} sprints</p>
                        }
                      }
                      @case (4) {
                        @if (data.workload; as rows) {
                          <table mat-table [dataSource]="rows" class="mini-table">
                            <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Person</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
                            <ng-container matColumnDef="openCount"><th mat-header-cell *matHeaderCellDef>Open</th><td mat-cell *matCellDef="let r">{{ r.openCount }}</td></ng-container>
                            <tr mat-header-row *matHeaderRowDef="['employeeName','openCount']"></tr>
                            <tr mat-row *matRowDef="let row; columns: ['employeeName','openCount']"></tr>
                          </table>
                        }
                      }
                      @case (6) {
                        @if (data.deadlineDashboard; as d) {
                          <div class="mini-stats">
                            <span class="overdue">Overdue {{ d.overdue }}</span>
                            <span>Due today {{ d.dueToday }}</span>
                            <span>{{ d.overallHealthLabel }}</span>
                          </div>
                        }
                      }
                      @case (7) {
                        @if (data.overdueRows; as rows) {
                          <ul class="plain-list compact">
                            @for (r of rows.slice(0, 5); track r.id) {
                              <li>{{ r.keyOrTitle }} · {{ r.delayLabel }}</li>
                            }
                          </ul>
                        }
                      }
                      @case (8) {
                        @if (data.projectHealth; as h) {
                          <div class="mini-stats">
                            <span>{{ h.completionPercent }}% complete</span>
                            <span>Blocked {{ h.blocked }}</span>
                            <span>{{ h.healthLabel }}</span>
                          </div>
                        }
                      }
                      @case (9) {
                        @if (data.sprintProgress; as sp) {
                          <div class="mini-stats">
                            <span>{{ sp.sprintName || 'Sprint' }}</span>
                            <span>{{ sp.completedPoints || 0 }}/{{ sp.totalPoints || 0 }} pts</span>
                            <span>{{ sp.percentComplete || 0 }}%</span>
                          </div>
                        }
                      }
                      @case (10) {
                        @if (data.listItems; as rows) {
                          <ul class="plain-list compact">
                            @for (r of rows.slice(0, 8); track $index) {
                              <li><strong>{{ r.key }}</strong> {{ r.summary }}</li>
                            }
                          </ul>
                        }
                      }
                      @case (11) {
                        @if (data.listItems; as rows) {
                          <ul class="plain-list compact">
                            @for (r of rows.slice(0, 8); track $index) {
                              <li>{{ r.name || '' }} {{ r.message || r.summary }}</li>
                            }
                          </ul>
                        }
                      }
                      @case (12) {
                        @if (data.timeLogged; as t) {
                          <div class="mini-stats"><span>Total {{ t.totalHours || 0 }}h</span></div>
                          <ul class="plain-list compact">
                            @for (i of (t.items || []).slice(0, 6); track $index) {
                              <li>{{ i.name || '—' }} · {{ i.hours || 0 }}h</li>
                            }
                          </ul>
                        } @else if (data.chartPoints?.length) {
                          <div class="dist-bars">
                            @for (p of data.chartPoints!; track p.label) {
                              <div class="dist-row">
                                <span>{{ p.label }}</span>
                                <div class="dist-track"><div class="dist-fill" [style.width.%]="(p.value / maxPoint(data.chartPoints!)) * 100"></div></div>
                                <span>{{ p.value }}</span>
                              </div>
                            }
                          </div>
                        }
                      }
                      @case (14) {
                        @if (data.chartPoints?.length) {
                          <div class="dist-bars">
                            @for (p of data.chartPoints!; track p.label) {
                              <div class="dist-row">
                                <span>{{ p.label }}</span>
                                <div class="dist-track"><div class="dist-fill" [style.width.%]="(p.value / maxPoint(data.chartPoints!)) * 100"></div></div>
                                <span>{{ p.value }}</span>
                              </div>
                            }
                          </div>
                        }
                      }
                      @case (15) {
                        @if (data.riskReport; as risk) {
                          <div class="mini-stats">
                            <span>High {{ risk.high || 0 }}</span>
                            <span>Med {{ risk.medium || 0 }}</span>
                            <span>Low {{ risk.low || 0 }}</span>
                          </div>
                          <ul class="plain-list compact">
                            @for (r of (risk.items || []).slice(0, 5); track $index) {
                              <li>{{ r.key }} · {{ r.risk }}</li>
                            }
                          </ul>
                        }
                      }
                      @default {
                        @if (data.chartPoints?.length) {
                          <div class="dist-bars">
                            @for (p of data.chartPoints!; track p.label) {
                              <div class="dist-row">
                                <span>{{ p.label }}</span>
                                <div class="dist-track"><div class="dist-fill" [style.width.%]="(p.value / maxPoint(data.chartPoints!)) * 100"></div></div>
                                <span>{{ p.value }}</span>
                              </div>
                            }
                          </div>
                        } @else if (data.listItems?.length) {
                          <ul class="plain-list compact">
                            @for (r of data.listItems!.slice(0, 8); track $index) {
                              <li>{{ r.key || r.name || '' }} {{ r.summary || r.message || r.label || '' }}</li>
                            }
                          </ul>
                        } @else {
                          <p class="muted">No widget data available.</p>
                        }
                      }
                    }
                  } @else {
                    <p class="muted">Loading widget…</p>
                  }
                </article>
              } @empty { <p class="muted">No widgets yet.</p> }
            </div>
          </section>
        }
      </div>
    </mat-tab>

    <mat-tab label="Sprint report">
      <div class="tab-body">
        <mat-form-field appearance="outline">
          <mat-label>Sprint</mat-label>
          <mat-select [value]="selectedSprintId()" (selectionChange)="onSprintChange($event.value)">
            @for (sp of sprints(); track sp.id) {
              <mat-option [value]="sp.id">{{ sp.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (sprintReport(); as r) {
          <div class="stats">
            <div class="stat"><span>Sprint</span><strong>{{ r.sprintName }}</strong></div>
            <div class="stat"><span>Committed</span><strong>{{ r.committedPoints ?? 0 }}</strong></div>
            <div class="stat"><span>Completed</span><strong>{{ r.completedPoints ?? 0 }}</strong></div>
            <div class="stat"><span>Completion</span><strong>{{ r.completionPercent ?? 0 }}%</strong></div>
          </div>
          @if (r.items?.length) {
            <div class="dist-bars">
              @for (p of r.items!; track p.label) {
                <div class="dist-row">
                  <span>{{ p.label }}</span>
                  <div class="dist-track"><div class="dist-fill" [style.width.%]="(p.value / maxPoint(r.items!)) * 100"></div></div>
                  <span>{{ p.value }}</span>
                </div>
              }
            </div>
          }
        } @else { <p class="muted">Select a sprint or data may not be available yet.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Progress">
      <div class="tab-body">
        @if (progressReport(); as r) {
          <div class="stat inline-stat"><span>Overall completion</span><strong>{{ r.completionPercent ?? 0 }}%</strong></div>
          @if (r.byStatus?.length) {
            <h4>By status</h4>
            <div class="dist-bars">
              @for (item of r.byStatus!; track item.label) {
                <div class="dist-row">
                  <span>{{ item.label }}</span>
                  <div class="dist-track"><div class="dist-fill" [style.width.%]="(item.count / maxCount(r.byStatus!)) * 100"></div></div>
                  <span>{{ item.count }}</span>
                </div>
              }
            </div>
          }
          @if (r.byType?.length) {
            <h4>By type</h4>
            <div class="dist-bars">
              @for (item of r.byType!; track item.label) {
                <div class="dist-row">
                  <span>{{ item.label }}</span>
                  <div class="dist-track"><div class="dist-fill alt" [style.width.%]="(item.count / maxCount(r.byType!)) * 100"></div></div>
                  <span>{{ item.count }}</span>
                </div>
              }
            </div>
          }
        } @else { <p class="muted">Progress report not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Issue distribution">
      <div class="tab-body">
        @if (issueDistribution(); as r) {
          <div class="dist-bars">
            @for (item of r.items; track item.label) {
              <div class="dist-row">
                <span>{{ item.label }}</span>
                <div class="dist-track"><div class="dist-fill" [style.width.%]="(item.count / maxCount(r.items)) * 100"></div></div>
                <span>{{ item.count }}@if (item.percent != null) { ({{ item.percent }}%) }</span>
              </div>
            }
          </div>
        } @else { <p class="muted">Issue distribution not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Priority distribution">
      <div class="tab-body">
        @if (priorityDistribution(); as r) {
          <div class="dist-bars">
            @for (item of r.items; track item.label) {
              <div class="dist-row">
                <span>{{ item.label }}</span>
                <div class="dist-track"><div class="dist-fill alt" [style.width.%]="(item.count / maxCount(r.items)) * 100"></div></div>
                <span>{{ item.count }}</span>
              </div>
            }
          </div>
        } @else { <p class="muted">Priority distribution not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Bug trend">
      <div class="tab-body">
        @if (bugTrend(); as r) {
          <div class="totals" style="display:flex;gap:12px;margin-bottom:12px;flex-wrap:wrap">
            <span class="stat-chip">Open bugs: {{ r.openBugs ?? 0 }}</span>
            <span class="stat-chip">Closed bugs: {{ r.closedBugs ?? 0 }}</span>
          </div>
          <div class="bars">
            @for (p of r.points; track p.label) {
              <div class="bar-col" [title]="p.label + ': ' + p.value">
                <div class="bar completed" [style.height.%]="(p.value / maxPoint(r.points)) * 100"></div>
                <span>{{ p.label }}</span>
              </div>
            }
          </div>
        } @else { <p class="muted">Bug trend not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Throughput">
      <div class="tab-body">
        <div class="export-row">
          <button mat-stroked-button type="button" [disabled]="exporting() === 'throughput'" (click)="exportCsv('throughput')">
            <mat-icon>download</mat-icon> Export throughput
          </button>
        </div>
        @if (throughput(); as t) {
          <div class="bars">
            @for (w of t.weekly; track w.weekStart) {
              <div class="bar-col" [title]="w.weekStart + ': ' + w.completedIssues + ' issues / ' + w.completedPoints + ' pts'">
                <div class="bar completed" [style.height.%]="throughputBarHeight(w.completedIssues, t.weekly)"></div>
                <span>{{ w.weekStart | slice:5:10 }}</span>
                <span class="muted">{{ w.completedIssues }}</span>
              </div>
            }
          </div>
          <p class="legend">Completed issues (and points) per week · last {{ t.weeks }} weeks</p>
        } @else { <p class="muted">Throughput not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Time utilization">
      <div class="tab-body">
        @if (timeUtilization(); as r) {
          <p class="muted">Total {{ r.totalHours }} hours logged</p>
          <div class="dist-bars">
            @for (item of r.byEmployee; track $index) {
              <div class="dist-row">
                <span>{{ item.employeeName }}</span>
                <div class="dist-track"><div class="dist-fill" [style.width.%]="(item.totalHours / maxHours(r.totalHours, item.totalHours)) * 100"></div></div>
                <span>{{ item.totalHours }}h</span>
              </div>
            }
          </div>
        } @else { <p class="muted">Time utilization not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Resource utilization">
      <div class="tab-body">
        <table mat-table [dataSource]="resourceUtilization()?.assignees ?? []" class="full">
          <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Employee</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
          <ng-container matColumnDef="openIssues"><th mat-header-cell *matHeaderCellDef>Open</th><td mat-cell *matCellDef="let r">{{ r.openIssues }}</td></ng-container>
          <ng-container matColumnDef="remainingStoryPoints"><th mat-header-cell *matHeaderCellDef>Remaining pts</th><td mat-cell *matCellDef="let r">{{ r.remainingStoryPoints }}</td></ng-container>
          <ng-container matColumnDef="availableHours"><th mat-header-cell *matHeaderCellDef>Available hrs</th><td mat-cell *matCellDef="let r">{{ r.availableHours }}</td></ng-container>
          <ng-container matColumnDef="isOverallocated"><th mat-header-cell *matHeaderCellDef>Overallocated</th><td mat-cell *matCellDef="let r">{{ r.isOverallocated ? 'Yes' : 'No' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="resourceColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: resourceColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Risk report">
      <div class="tab-body">
        <table mat-table [dataSource]="riskReport()?.items ?? []" class="full">
          <ng-container matColumnDef="keyOrTitle"><th mat-header-cell *matHeaderCellDef>Item</th><td mat-cell *matCellDef="let r">{{ r.keyOrTitle }}</td></ng-container>
          <ng-container matColumnDef="riskLevel"><th mat-header-cell *matHeaderCellDef>Risk</th><td mat-cell *matCellDef="let r">{{ r.riskLevel || '—' }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel || '—' }}</td></ng-container>
          <ng-container matColumnDef="assigneeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let r">{{ r.assigneeName || '—' }}</td></ng-container>
          <ng-container matColumnDef="dueDate"><th mat-header-cell *matHeaderCellDef>Due</th><td mat-cell *matCellDef="let r">{{ r.dueDate || '—' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="riskColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: riskColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Cumulative flow">
      <div class="tab-body">
        <div class="export-row">
          <mat-form-field appearance="outline">
            <mat-label>Export format</mat-label>
            <mat-select [(ngModel)]="exportFormat">
              <mat-option value="csv">CSV</mat-option>
              <mat-option value="xlsx">Excel</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" [disabled]="exporting() === 'cfd'" (click)="exportCsv('cfd')">
            <mat-icon>download</mat-icon> Export
          </button>
        </div>
        @if (cumulativeFlow(); as cfd) {
          <svg class="svg-chart wide" viewBox="0 0 360 180" role="img" aria-label="Cumulative flow diagram">
            <polyline fill="none" stroke="#94a3b8" stroke-width="2" [attr.points]="svgCfdLine(cfd.points, 'toDo')" />
            <polyline fill="none" stroke="#f59e0b" stroke-width="2" [attr.points]="svgCfdLine(cfd.points, 'inProgress')" />
            <polyline fill="none" stroke="#0f766e" stroke-width="2.5" [attr.points]="svgCfdLine(cfd.points, 'done')" />
          </svg>
          <p class="legend">
            <span class="swatch ideal"></span> To Do
            <span class="swatch" style="background:#f59e0b"></span> In Progress
            <span class="swatch remaining"></span> Done
          </p>
          <table class="cfd-table">
            <thead>
              <tr><th>Date</th>@for (label of cfdStatusLabels(); track label) { <th>{{ label }}</th> }</tr>
            </thead>
            <tbody>
              @for (day of cfd.points; track day.date) {
                <tr>
                  <td>{{ day.date }}</td>
                  @for (label of cfdStatusLabels(); track label) {
                    <td>{{ cfdCount(day, label) }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        } @else { <p class="muted">Cumulative flow data not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Cycle / Lead time">
      <div class="tab-body">
        <div class="export-row">
          <button mat-stroked-button type="button" [disabled]="exporting() === 'cycle-time'" (click)="exportCsv('cycle-time')">Export cycle CSV</button>
          <button mat-stroked-button type="button" [disabled]="exporting() === 'lead-time'" (click)="exportCsv('lead-time')">Export lead CSV</button>
        </div>
        @if (cycleTime(); as ct) {
          <h4>Cycle time — avg {{ ct.averageDays ?? '—' }}d · median {{ ct.medianDays ?? '—' }}d</h4>
          <div class="dist-bars">
            @for (b of ct.distribution; track b.label) {
              <div class="dist-row">
                <span>{{ b.label }}</span>
                <div class="dist-track"><div class="dist-fill" [style.width.%]="(b.count / maxCount(ct.distribution)) * 100"></div></div>
                <span>{{ b.count }}</span>
              </div>
            }
          </div>
        }
        @if (leadTime(); as lt) {
          <h4>Lead time — avg {{ lt.averageDays ?? '—' }}d · median {{ lt.medianDays ?? '—' }}d</h4>
          <div class="dist-bars">
            @for (b of lt.distribution; track b.label) {
              <div class="dist-row">
                <span>{{ b.label }}</span>
                <div class="dist-track"><div class="dist-fill alt" [style.width.%]="(b.count / maxCount(lt.distribution)) * 100"></div></div>
                <span>{{ b.count }}</span>
              </div>
            }
          </div>
        }
        @if (!cycleTime() && !leadTime()) { <p class="muted">Cycle/lead time reports not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Forecast">
      <div class="tab-body">
        @if (forecast(); as f) {
          <div class="stats">
            <div class="stat"><span>Remaining pts</span><strong>{{ f.remainingPoints ?? '—' }}</strong></div>
            <div class="stat"><span>Avg velocity</span><strong>{{ f.averageVelocity ?? '—' }}</strong></div>
            <div class="stat"><span>Sprints left</span><strong>{{ f.sprintsRemaining ?? '—' }}</strong></div>
            <div class="stat"><span>ETA</span><strong>{{ f.estimatedCompletionDate || '—' }}</strong></div>
            <div class="stat"><span>Confidence</span><strong>{{ f.confidenceLabel || '—' }}</strong></div>
          </div>
        } @else { <p class="muted">Forecast not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Blocked">
      <div class="tab-body">
        <div class="export-row">
          <button mat-stroked-button type="button" [disabled]="exporting() === 'blocked'" (click)="exportCsv('blocked')">
            <mat-icon>download</mat-icon> Export CSV
          </button>
        </div>
        <table mat-table [dataSource]="blockedIssues()" class="full">
          <ng-container matColumnDef="keyOrTitle"><th mat-header-cell *matHeaderCellDef>Issue</th><td mat-cell *matCellDef="let r">{{ r.keyOrTitle }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel || '—' }}</td></ng-container>
          <ng-container matColumnDef="assigneeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let r">{{ r.assigneeName || '—' }}</td></ng-container>
          <ng-container matColumnDef="blockedSince"><th mat-header-cell *matHeaderCellDef>Blocked since</th><td mat-cell *matCellDef="let r">{{ r.blockedSince || '—' }}</td></ng-container>
          <ng-container matColumnDef="blockedReason"><th mat-header-cell *matHeaderCellDef>Reason</th><td mat-cell *matCellDef="let r">{{ r.blockedReason || '—' }}</td></ng-container>
          <ng-container matColumnDef="dueDate"><th mat-header-cell *matHeaderCellDef>Due</th><td mat-cell *matCellDef="let r">{{ r.dueDate || '—' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="blockedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: blockedColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Milestones">
      <div class="tab-body">
        <div class="export-row">
          <button mat-stroked-button type="button" [disabled]="exporting() === 'milestones'" (click)="exportCsv('milestones')">
            <mat-icon>download</mat-icon> Export
          </button>
        </div>
        <table mat-table [dataSource]="milestoneProgress()?.milestones ?? []" class="full">
          <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Milestone</th><td mat-cell *matCellDef="let r">{{ r.name }}</td></ng-container>
          <ng-container matColumnDef="targetDate"><th mat-header-cell *matHeaderCellDef>Target</th><td mat-cell *matCellDef="let r">{{ r.targetDate || '—' }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel || r.status || '—' }}</td></ng-container>
          <ng-container matColumnDef="completionPercent"><th mat-header-cell *matHeaderCellDef>Progress</th><td mat-cell *matCellDef="let r">{{ r.completionPercent != null ? r.completionPercent + '%' : '—' }}</td></ng-container>
          <ng-container matColumnDef="completedCount"><th mat-header-cell *matHeaderCellDef>Done/Total</th><td mat-cell *matCellDef="let r">@if (r.totalCount != null) { {{ r.completedCount ?? 0 }}/{{ r.totalCount }} } @else { — }</td></ng-container>
          <ng-container matColumnDef="versionName"><th mat-header-cell *matHeaderCellDef>Version</th><td mat-cell *matCellDef="let r">{{ r.versionName || '—' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="milestoneColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: milestoneColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Release progress">
      <div class="tab-body">
        <div class="export-row">
          <button mat-stroked-button type="button" [disabled]="exporting() === 'releases'" (click)="exportCsv('releases')">
            <mat-icon>download</mat-icon> Export
          </button>
        </div>
        <table mat-table [dataSource]="releaseProgress()?.releases ?? []" class="full">
          <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Release</th><td mat-cell *matCellDef="let r">{{ r.name }}</td></ng-container>
          <ng-container matColumnDef="releaseDate"><th mat-header-cell *matHeaderCellDef>Release date</th><td mat-cell *matCellDef="let r">{{ r.releaseDate || r.targetDate || '—' }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let r">{{ r.statusLabel || r.status || '—' }}</td></ng-container>
          <ng-container matColumnDef="completionPercent"><th mat-header-cell *matHeaderCellDef>Progress</th><td mat-cell *matCellDef="let r">{{ r.completionPercent != null ? r.completionPercent + '%' : '—' }}</td></ng-container>
          <ng-container matColumnDef="issueCount"><th mat-header-cell *matHeaderCellDef>Issues</th><td mat-cell *matCellDef="let r">{{ r.issueCount ?? '—' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="releaseColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: releaseColumns"></tr>
        </table>
      </div>
    </mat-tab>

    <mat-tab label="Capacity">
      <div class="tab-body">
        @if (capacity(); as cap) {
          <div class="stats">
            <div class="stat"><span>Sprint capacity</span><strong>{{ cap.sprintCapacityPoints }}</strong></div>
            <div class="stat"><span>Assigned open</span><strong>{{ cap.assignedOpenPoints }}</strong></div>
            <div class="stat"><span>Available hrs</span><strong>{{ cap.availableHours ?? '—' }}</strong></div>
            <div class="stat"><span>Working days</span><strong>{{ cap.workingDays ?? '—' }}</strong></div>
            <div class="stat overdue"><span>Overallocated</span><strong>{{ cap.overallocatedAssigneeCount ?? 0 }}</strong></div>
          </div>
          <table mat-table [dataSource]="cap.assigneeLoad" class="full">
            <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
            <ng-container matColumnDef="openIssues"><th mat-header-cell *matHeaderCellDef>Open</th><td mat-cell *matCellDef="let r">{{ r.openIssues }}</td></ng-container>
            <ng-container matColumnDef="remainingStoryPoints"><th mat-header-cell *matHeaderCellDef>Remaining pts</th><td mat-cell *matCellDef="let r">{{ r.remainingStoryPoints }}</td></ng-container>
            <ng-container matColumnDef="availableHours"><th mat-header-cell *matHeaderCellDef>Available hrs</th><td mat-cell *matCellDef="let r">{{ r.availableHours }}</td></ng-container>
            <ng-container matColumnDef="leaveDays"><th mat-header-cell *matHeaderCellDef>Leave days</th><td mat-cell *matCellDef="let r">{{ r.leaveDays ?? 0 }}</td></ng-container>
            <ng-container matColumnDef="isOverallocated"><th mat-header-cell *matHeaderCellDef>Overallocated</th><td mat-cell *matCellDef="let r">{{ r.isOverallocated ? 'Yes' : 'No' }}</td></ng-container>
            <tr mat-header-row *matHeaderRowDef="capacityColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: capacityColumns"></tr>
          </table>
        } @else { <p class="muted">Capacity report not available.</p> }
      </div>
    </mat-tab>

    <mat-tab label="Productivity">
      <div class="tab-body">
        @if (productivity(); as prod) {
          <div class="stats">
            <div class="stat"><span>Team completion</span><strong>{{ prod.teamAverageCompletionRate }}%</strong></div>
            <div class="stat"><span>Team velocity contrib</span><strong>{{ prod.teamAverageVelocityContribution }}</strong></div>
          </div>
          <table mat-table [dataSource]="prod.employees" class="full">
            <ng-container matColumnDef="employeeName"><th mat-header-cell *matHeaderCellDef>Employee</th><td mat-cell *matCellDef="let r">{{ r.employeeName }}</td></ng-container>
            <ng-container matColumnDef="issuesCompleted"><th mat-header-cell *matHeaderCellDef>Done</th><td mat-cell *matCellDef="let r">{{ r.issuesCompleted }}</td></ng-container>
            <ng-container matColumnDef="issuesOpen"><th mat-header-cell *matHeaderCellDef>Open</th><td mat-cell *matCellDef="let r">{{ r.issuesOpen }}</td></ng-container>
            <ng-container matColumnDef="storyPointsCompleted"><th mat-header-cell *matHeaderCellDef>SP done</th><td mat-cell *matCellDef="let r">{{ r.storyPointsCompleted }}</td></ng-container>
            <ng-container matColumnDef="hoursLogged"><th mat-header-cell *matHeaderCellDef>Hours</th><td mat-cell *matCellDef="let r">{{ r.hoursLogged }}</td></ng-container>
            <ng-container matColumnDef="completionRatePercent"><th mat-header-cell *matHeaderCellDef>Rate</th><td mat-cell *matCellDef="let r">{{ r.completionRatePercent }}%</td></ng-container>
            <ng-container matColumnDef="averageDelayDays"><th mat-header-cell *matHeaderCellDef>Avg delay</th><td mat-cell *matCellDef="let r">{{ r.averageDelayDays }}d</td></ng-container>
            <ng-container matColumnDef="overdueTasks"><th mat-header-cell *matHeaderCellDef>Overdue</th><td mat-cell *matCellDef="let r">{{ r.overdueTasks }}</td></ng-container>
            <tr mat-header-row *matHeaderRowDef="productivityColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: productivityColumns"></tr>
          </table>
        } @else { <p class="muted">Productivity report not available.</p> }
      </div>
    </mat-tab>
  </mat-tab-group>
</section>
